import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import betaAPI from "v2/api";
import V2AuthInput from "./components/V2AuthInput";
import V2AuthButton from "./components/V2AuthButton";

export default function V2ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await betaAPI.requestPasswordReset({ email });
      setIsSubmitted(true);
    } catch (err) {
      console.error("Password reset request error:", err);
      setError(err.message || t("auth.forgotPassword.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Success state after submission
  if (isSubmitted) {
    return (
      <>
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle size={28} className="text-green-600" />
          </div>
        </div>

        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {t("auth.common.checkYourEmail")}
          </h1>
          <p className="text-sm text-[#6B7280]">
            <Trans
              i18nKey="auth.forgotPassword.sentTo"
              values={{ email }}
              components={{
                email: <span className="font-medium text-[#374151]" />,
              }}
            />
          </p>
        </div>

        {/* Info box */}
        <div
          className="mb-6 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <p className="text-sm text-[#374151]">
            {t("auth.forgotPassword.info")}
          </p>
        </div>

        {/* Back to login */}
        <V2AuthButton
          variant="secondary"
          onClick={() => navigate("/v2/auth/login")}
        >
          <span className="flex items-center justify-center gap-2">
            <ArrowLeft size={16} />
            {t("auth.common.backToLogin")}
          </span>
        </V2AuthButton>

        {/* Resend option */}
        <div className="mt-4 text-center">
          <p className="text-sm text-[#6B7280]">
            {t("auth.forgotPassword.didntReceive")}{" "}
            <button
              onClick={() => setIsSubmitted(false)}
              className="font-medium text-[#E07A3A] transition-colors hover:text-[#D66B2E]"
            >
              {t("auth.forgotPassword.tryAgain")}
            </button>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Icon */}
      <div className="mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FEF3E7]">
          <Mail size={28} className="text-[#E07A3A]" />
        </div>
      </div>

      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.forgotPassword.title")}
        </h1>
        <p className="text-sm text-[#6B7280]">
          {t("auth.forgotPassword.subtitle")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.common.email")}
          </label>
          <V2AuthInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.common.emailPlaceholder")}
            error={!!error}
            disabled={isLoading}
            autoComplete="email"
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Submit button */}
        <V2AuthButton
          type="submit"
          variant="primary"
          disabled={!email.trim()}
          loading={isLoading}
        >
          {t("auth.forgotPassword.submit")}
        </V2AuthButton>
      </form>

      {/* Back to login */}
      <div className="mt-6 text-center">
        <Link
          to="/v2/auth/login"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] transition-colors hover:text-[#374151]"
        >
          <ArrowLeft size={16} />
          {t("auth.forgotPassword.backToSignIn")}
        </Link>
      </div>
    </>
  );
}
